import AnimatedNumber from '@/components/animated-number';
import PlatformReferenceInput from '@/components/platform-reference-input';
import { useI18n } from '@/lib/i18n';
import { OrderInteraction, OrderPackage, OrderPlatform, PlatformReference } from '@/types';
import { Page } from '@inertiajs/core';
import { useForm } from '@inertiajs/react';
import { FormEvent, useEffect, useMemo, useState } from 'react';

export default function ProductForm({
  packages,
  platform,
  interaction,
  cheapestPackage,
  recommendedPackage,
  onSuccess,
}: {
  packages: OrderPackage[];
  platform: OrderPlatform;
  interaction: OrderInteraction;
  cheapestPackage: OrderPackage;
  recommendedPackage: OrderPackage;
  onSuccess?: (e: Page) => void;
}) {
  const { t, formatNumber } = useI18n();
  const [size, setSize] = useState(recommendedPackage.size);
  const [platformReference, setPlatformReference] = useState<PlatformReference | null>(null);
  const selectedPackage = useMemo(() => packages.find((p) => p.size === size)!, [size]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const sizeFromQuery = searchParams.get('size');

    if (sizeFromQuery && !isNaN(Number(sizeFromQuery))) {
      setSize(Number(sizeFromQuery));
    }
  }, []);

  return (
    <div
      id={'product-form'}
      className="border-secondary -mx-6 border-y-2 bg-white p-6 shadow-2xl shadow-yellow-900/20 md:mx-0 md:rounded-3xl md:border-2"
    >
      <div className="grid grid-cols-2">
        <div className={'text-left'}>
          <h2 className={'text-2xl font-bold md:text-3xl'}>
            <AnimatedNumber value={selectedPackage.size} formatFn={(n) => formatNumber(n, false, 0)} />
          </h2>
          <h3 className={'font-semibold text-gray-500'}>{t(`packages.${interaction}`)}</h3>
        </div>

        <div className={'text-left'}>
          <h2 className={'text-2xl font-bold md:text-3xl'}>
            <AnimatedNumber value={selectedPackage.price} formatFn={(n) => formatNumber(n, true)} />
          </h2>
          <h3 className={'font-semibold text-gray-500'}>{t('components.products_form.Your price')}</h3>
        </div>
      </div>

      {platformReference && (
        <DetailsForm
          platform={platform}
          interaction={interaction}
          size={size}
          platformReference={platformReference}
          resetPlatformReference={() => setPlatformReference(null)}
          onSuccess={onSuccess}
        />
      )}

      <div className={platformReference ? 'hidden' : ''}>
        <div className="mt-4 grid grid-cols-3 gap-3 md:grid-cols-4">
          {packages.map((pack, n) => (
            <div
              key={pack.size}
              role="button"
              id={`size-${pack.size}`}
              className={`relative flex w-full cursor-pointer flex-col justify-center rounded-md py-3 ${pack.size === selectedPackage.size ? 'bg-secondary/10 ring-secondary ring-2' : 'ring ring-gray-300'}`}
              onClick={() => setSize(pack.size)}
            >
              <div className={'text-center text-lg font-black'}>{formatNumber(pack.size)}</div>

              {recommendedPackage.size === pack.size && (
                <div className="bg-secondary absolute -top-1.5 right-1 left-1 rounded-full md:-top-2 md:py-0.5">
                  <div className="text-center text-xs font-semibold text-yellow-950 md:text-xs">{t('components.products_form.Most popular')}</div>
                </div>
              )}

              {n > 0 && (
                <div className="flex justify-center">
                  <div className={'flex items-center justify-center rounded-xl bg-green-200 px-2 py-0.5 text-green-950'}>
                    <div className={'text-xs font-semibold'}>{t('components.products_form.:amount % off', { amount: pack.percentOff })}</div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <PlatformReferenceInput
          platform={platform}
          interaction={interaction}
          deliveryInMinutes={cheapestPackage.deliveryInMinutes}
          rating={cheapestPackage.rating}
          onChange={(platformReference) => {
            setPlatformReference(platformReference);
            document.getElementById('product-form')?.scrollIntoView({ behavior: 'smooth' });
          }}
        />
      </div>
    </div>
  );
}

function DetailsForm({
  platform,
  interaction,
  size,
  platformReference,
  resetPlatformReference,
  onSuccess,
}: {
  platform: OrderPlatform;
  interaction: OrderInteraction;
  size: number;
  platformReference: PlatformReference;
  resetPlatformReference: () => void;
  onSuccess?: (e: Page) => void;
}) {
  const { t } = useI18n();
  const { data, setData, post, processing, errors, clearErrors } = useForm({
    platform,
    interaction,
    size,
    platform_reference: platformReference as unknown as string, // some error in Inertia
    email: '',
    coupon_code: '',
  });

  function submit(e: FormEvent<HTMLFormElement> | null = null) {
    if (processing) return;

    e?.preventDefault();

    post(route('orders.store'), {
      preserveScroll: 'errors',
      onSuccess,
    });
  }

  return (
    <form onSubmit={submit}>
      <div className={'mt-4'}>
        <div className={'font-semibold text-gray-700'}>
          {interaction === OrderInteraction.Followers ? t('components.products_form.Chosen account') : t('components.products_form.Chosen post')}:
        </div>
        <div className={'flex items-center overflow-hidden rounded-xl border border-gray-300 p-1.5'}>
          <img src={platformReference.post?.thumbnail ?? platformReference.user.avatar} alt="Reference" className={'size-8 rounded-lg'} />

          <div className={'flex-1 truncate px-1.5 text-sm'}>{platformReference.url}</div>

          <button className={'p-1 text-sm font-semibold text-black hover:underline'} onClick={resetPlatformReference}>
            {t('components.products_form.Change')}
          </button>
        </div>
        {errors.platform_reference && <div className={'mt-1 rounded bg-red-50 p-2 text-xs text-red-950'}>{errors.platform_reference}</div>}
      </div>

      <div className={'mt-4'}>
        <label htmlFor="email" className={'font-semibold text-gray-700'}>
          {t('components.products_form.Email')}:
        </label>

        <div className="flex items-center rounded-xl border border-gray-300">
          <input
            id="email"
            name="email"
            type="email"
            className={'w-full p-2.5 focus:outline-0 md:text-lg'}
            required={true}
            autoComplete="email"
            placeholder={'some@email.com'}
            value={data.email}
            onChange={(e) => {
              setData('email', e.target.value);
              clearErrors('email');
            }}
          />
        </div>
        {errors.email && <div className={'mt-1 rounded bg-red-50 p-2 text-xs text-red-950'}>{errors.email}</div>}
      </div>

      <div className="mt-4">
        <label htmlFor="coupon_code" className={'font-semibold text-gray-700'}>
          {t('components.products_form.Coupon code')}
        </label>

        <div className="flex items-center rounded-xl border border-gray-300">
          <input
            id="coupon_code"
            name="coupon_code"
            type="text"
            className={'w-full p-2.5 focus:outline-0 md:text-lg'}
            placeholder={t('components.products_form.Have a coupon code? Enter it here')}
            value={data.coupon_code}
            onChange={(e) => {
              setData('coupon_code', e.target.value);
              clearErrors('coupon_code');
            }}
          />
        </div>
        {errors.coupon_code && <div className={'mt-1 rounded bg-red-50 p-2 text-xs text-red-950'}>{errors.coupon_code}</div>}
      </div>

      <div className="mt-4">
        <button
          type={'submit'}
          className={'w-full rounded-xl bg-black px-4 py-3 text-center font-bold text-white not-disabled:hover:opacity-75 disabled:bg-gray-300'}
          disabled={processing}
        >
          {t('components.products_form.Go to checkout')} &rarr;
        </button>
      </div>
    </form>
  );
}
